import type {
  AccountConnection,
  AccountQuizPage,
  AccountQuizSummary,
  AddQuizShareResponse,
  AppConfig,
  AuthSession,
  ClaimItem,
  ClaimResult,
  GameResult,
  GameResultSummary,
  QuizContent,
  QuizPersonRef,
  QuizRecord,
  QuizResponse,
  QuizRole,
  QuizSharing,
  QuizSummary,
  SessionUser,
} from '@shared/types';

export class ApiError extends Error {
  /** A 409 from /api/account: this browser is signed in to another account than the one the page shows. */
  accountChanged = false;

  constructor(
    public status: number,
    message: string,
    public issues: string[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Sent on every write. The server only counts a write as signed in when it carries this header
 * (a custom header forces a CORS preflight, so other sites can't make the browser send it).
 */
const CSRF_HEADER = 'X-Requested-With';
const CSRF_VALUE = 'quiz-dayro';

/**
 * Sent on /api/account requests: the id of the account the page shows. The server answers 409
 * when this browser is signed in to another account by now (switched in another tab).
 */
const ACCOUNT_HEADER = 'X-Account-Id';

const unauthorizedListeners = new Set<() => void>();
const accountChangedListeners = new Set<() => void>();

/** Called on every 401 (the session ended); the auth provider re-reads the session. Returns an unsubscribe. */
export function onUnauthorized(listener: () => void): () => void {
  unauthorizedListeners.add(listener);
  return () => {
    unauthorizedListeners.delete(listener);
  };
}

/** Called when an account request finds another account signed in (409); the auth provider re-reads the session. */
export function onAccountChanged(listener: () => void): () => void {
  accountChangedListeners.add(listener);
  return () => {
    accountChangedListeners.delete(listener);
  };
}

/** The 409 of an account request made for an account that is no longer the signed-in one. */
export function isAccountChanged(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.accountChanged;
}

async function call<T>(
  path: string,
  init: RequestInit & { editKey?: string | null; account?: string } = {},
): Promise<T> {
  const { editKey, account, headers, ...rest } = init;
  const h = new Headers(headers);
  if (editKey) h.set('X-Edit-Key', editKey);
  if (account) h.set(ACCOUNT_HEADER, account);
  const method = (rest.method ?? 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD') h.set(CSRF_HEADER, CSRF_VALUE);
  if (rest.body && typeof rest.body === 'string' && !h.has('Content-Type')) h.set('Content-Type', 'application/json');
  let res: Response;
  try {
    res = await fetch(path, { ...rest, headers: h });
  } catch {
    throw new ApiError(0, 'Network error. Check your connection and try again.');
  }
  if (res.status === 204) return undefined as T;
  const body = await res.json().catch(() => null);
  if (res.ok && (body === null || typeof body !== 'object')) {
    // e.g. an SPA index.html fallback when /api is not routed to the server
    throw new ApiError(res.status, 'Unexpected response from the server.');
  }
  if (!res.ok) {
    if (res.status === 401) for (const listener of unauthorizedListeners) listener();
    const accountChanged = res.status === 409 && Boolean(account);
    if (accountChanged) for (const listener of accountChangedListeners) listener();
    const err = new ApiError(res.status, body?.error ?? `Request failed (${res.status})`, body?.issues ?? []);
    err.accountChanged = accountChanged;
    throw err;
  }
  return body as T;
}

const quizPath = (id: string) => `/api/quizzes/${encodeURIComponent(id)}`;

/** What the signed-in account may do with a quiz (GET /api/quizzes/:id), with its content. */
export interface QuizAccessView {
  quiz: QuizRecord;
  /** The signed-in account's role; null when signed out, or for anyone else (edit keys don't count). */
  role: QuizRole | null;
  /** role 'editor': who owns the quiz. */
  sharedBy: QuizPersonRef | null;
  /** role 'owner': people it is shared with, pending invites included. */
  shareCount: number | null;
  /** role === 'owner'. */
  ownedByYou: boolean;
}

/** Which quizzes an account list returns: its own, those shared with it, or both. */
export type AccountQuizScope = 'owned' | 'shared' | 'all';

/** The server takes at most this many quizzes per claim request. */
const CLAIM_BATCH = 100;
/** Page size for listing the account's quizzes (the server allows 1-500). */
const ACCOUNT_PAGE = 500;
/** Stops paging after this many quizzes. */
const ACCOUNT_MAX_QUIZZES = 10_000;

export const api = {
  config: () => call<AppConfig>('/api/config'),

  createQuiz: (content: QuizContent) =>
    call<{ quiz: QuizRecord; editKey: string }>('/api/quizzes', { method: 'POST', body: JSON.stringify(content) }),

  getQuiz: (id: string) => call<{ quiz: QuizRecord }>(quizPath(id)).then((r) => r.quiz),

  /**
   * The quiz plus what the signed-in account is to it (nothing when signed out). Sends the CSRF
   * header, so that opening a quiz shared with this account accepts the invitation; only the app's
   * own quiz page and editor ask this way, never a link or an image pointing at the API.
   */
  getQuizAccess: (id: string) =>
    call<Partial<QuizResponse> & { quiz: QuizRecord }>(quizPath(id), {
      headers: { [CSRF_HEADER]: CSRF_VALUE },
    }).then((r): QuizAccessView => {
      // An older server sends only ownedByYou.
      const role = r.role === 'owner' || r.role === 'editor' ? r.role : r.ownedByYou === true ? 'owner' : null;
      return {
        quiz: r.quiz,
        role,
        ownedByYou: role === 'owner',
        sharedBy: role === 'editor' ? (r.sharedBy ?? null) : null,
        shareCount: role === 'owner' ? (r.shareCount ?? 0) : null,
      };
    }),

  listQuizzes: (ids: string[]) =>
    ids.length === 0
      ? Promise.resolve([] as QuizSummary[])
      : call<{ quizzes: QuizSummary[] }>(`/api/quizzes?ids=${ids.map(encodeURIComponent).join(',')}`).then(
          (r) => r.quizzes,
        ),

  /**
   * Saves with the edit key, or as the owning account when `editKey` is null. With `version` (the
   * `updatedAt` the editor loaded), the server refuses with 409 if the quiz changed since then.
   */
  updateQuiz: (id: string, editKey: string | null, content: QuizContent, opts: { version?: string | null } = {}) =>
    call<{ quiz: QuizRecord }>(quizPath(id), {
      method: 'PUT',
      body: JSON.stringify(content),
      editKey,
      headers: opts.version ? { 'X-Quiz-Version': opts.version } : undefined,
    }).then((r) => r.quiz),

  deleteQuiz: (id: string, editKey: string | null) => call<void>(quizPath(id), { method: 'DELETE', editKey }),

  verifyKey: (id: string, editKey: string) =>
    call<{ valid: boolean }>(`${quizPath(id)}/verify-key`, { method: 'POST', editKey }).then((r) => r.valid),

  /** A new edit link for a quiz in your account; the old key stops working. Returns the new key. */
  newEditKey: (id: string) =>
    call<{ editKey: string }>(`${quizPath(id)}/edit-key`, { method: 'POST' }).then((r) => r.editKey),

  listResults: (quizId: string, editKey: string | null) =>
    call<{ results: GameResultSummary[] }>(`${quizPath(quizId)}/results`, { editKey }).then((r) => r.results),

  getResult: (quizId: string, resultId: string, editKey: string | null) =>
    call<{ result: GameResult }>(`${quizPath(quizId)}/results/${encodeURIComponent(resultId)}`, {
      editKey,
    }).then((r) => r.result),

  /** Upload already-resized image bytes. Returns the same-origin URL to store in the quiz. */
  uploadImage: (blob: Blob) =>
    call<{ id: string; url: string }>('/api/images', {
      method: 'POST',
      body: blob,
      headers: { 'Content-Type': blob.type || 'application/octet-stream' },
    }),

  // --- Google sign-in and the account ------------------------------------------------

  /** The signed-in account, or null (also when sign-in is off on this server). */
  session: () => call<AuthSession>('/api/auth/session').then((r): SessionUser | null => r.user ?? null),

  signOut: () => call<void>('/api/auth/signout', { method: 'POST' }),

  // The account routes take `account`: the id of the account the page shows (see ACCOUNT_HEADER).

  /** `scope` (default 'owned'): the account's own quizzes, those shared with it, or both. */
  myQuizzes: (
    account: string,
    params: { search?: string; limit?: number; offset?: number; scope?: AccountQuizScope } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.search) q.set('search', params.search);
    if (params.limit !== undefined) q.set('limit', String(params.limit));
    if (params.offset !== undefined) q.set('offset', String(params.offset));
    if (params.scope) q.set('scope', params.scope);
    const qs = q.toString();
    return call<AccountQuizPage>(`/api/account/quizzes${qs ? `?${qs}` : ''}`, { account });
  },

  /** Every quiz in the account (page by page), newest first; with 'all', also those shared with it. */
  allMyQuizzes: async (account: string, scope: 'owned' | 'all' = 'owned'): Promise<AccountQuizSummary[]> => {
    const all: AccountQuizSummary[] = [];
    let offset: number | null = 0;
    while (offset !== null && all.length < ACCOUNT_MAX_QUIZZES) {
      const page: AccountQuizPage = await api.myQuizzes(account, {
        limit: ACCOUNT_PAGE,
        offset,
        ...(scope === 'owned' ? {} : { scope }),
      });
      all.push(...page.quizzes);
      offset = page.quizzes.length > 0 ? page.nextOffset : null;
    }
    return all;
  },

  /** Moves quizzes this browser can edit into the account, in batches the server accepts. */
  claimQuizzes: async (account: string, items: ClaimItem[]): Promise<ClaimResult> => {
    const total: ClaimResult = { claimed: [], alreadyYours: [], ownedByOther: [], invalid: [] };
    for (let i = 0; i < items.length; i += CLAIM_BATCH) {
      const r = await call<ClaimResult>('/api/account/quizzes/claim', {
        method: 'POST',
        body: JSON.stringify({ quizzes: items.slice(i, i + CLAIM_BATCH) }),
        account,
      });
      total.claimed.push(...(r.claimed ?? []));
      total.alreadyYours.push(...(r.alreadyYours ?? []));
      total.ownedByOther.push(...(r.ownedByOther ?? []));
      total.invalid.push(...(r.invalid ?? []));
    }
    return total;
  },

  // --- Sharing a quiz by email (owner only; Leave is for the people it is shared with) ---

  /** Who a quiz in this account is shared with. */
  quizShares: (account: string, quizId: string) => call<QuizSharing>(`${quizPath(quizId)}/shares`, { account }),

  /** Shares a quiz in this account with one email address (201 new, 200 already there). */
  shareQuiz: (account: string, quizId: string, email: string) =>
    call<AddQuizShareResponse>(`${quizPath(quizId)}/shares`, {
      method: 'POST',
      body: JSON.stringify({ email }),
      account,
    }),

  removeQuizShare: (account: string, quizId: string, shareId: string) =>
    call<void>(`${quizPath(quizId)}/shares/${encodeURIComponent(shareId)}`, { method: 'DELETE', account }),

  /** Stops editing a quiz shared with this account (also declines an invitation not opened yet). */
  leaveQuiz: (account: string, quizId: string) => call<void>(`${quizPath(quizId)}/leave`, { method: 'POST', account }),

  connections: (account: string) =>
    call<{ connections: AccountConnection[] }>('/api/account/connections', { account }).then(
      (r) => r.connections ?? [],
    ),

  disconnect: (account: string, id: string) =>
    call<void>(`/api/account/connections/${encodeURIComponent(id)}`, { method: 'DELETE', account }),

  signOutEverywhere: (account: string) => call<void>('/api/account/signout-everywhere', { method: 'POST', account }),

  deleteAccount: (account: string, opts: { deleteQuizzes: boolean }) =>
    call<void>('/api/account', {
      method: 'DELETE',
      body: JSON.stringify({ deleteQuizzes: opts.deleteQuizzes }),
      account,
    }),
};
